/**
 * Schedule Date Utilities
 * 
 * Centralized date math for scheduling that:
 * - Uses local dates consistently (no UTC truncation risks)
 * - Supports working days (skipping weekends)
 * - Supports optional holiday calendars
 * - Provides stable formatting for YYYY-MM-DD strings
 */

/**
 * Calendar configuration for working day calculations
 */
export interface ScheduleCalendar {
  /** Days that are considered working days (0=Sunday, 1=Monday, ..., 6=Saturday) */
  workingDays: number[];
  /** Holidays to skip (YYYY-MM-DD format, local) */
  holidays: string[];
}

/**
 * Default calendar: Monday-Friday working days, no holidays
 */
export const DEFAULT_SCHEDULE_CALENDAR: ScheduleCalendar = {
  workingDays: [1, 2, 3, 4, 5], // Mon-Fri
  holidays: [],
};

/**
 * Format a Date object as YYYY-MM-DD using local time
 * 
 * This avoids the common pitfall of using toISOString().split('T')[0]
 * which uses UTC and can shift dates by ±1 day depending on timezone.
 * 
 * @param date - The date to format
 * @returns Date string in YYYY-MM-DD format (local time)
 */
export function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse a YYYY-MM-DD date string into a Date object at local midnight
 * 
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Date object set to midnight local time
 */
export function parseLocalDate(dateStr: string): Date {
  // Parse as local date (not UTC)
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

/**
 * Check if a date is a working day according to the calendar
 * 
 * @param date - The date to check
 * @param calendar - Calendar configuration (defaults to Mon-Fri, no holidays)
 * @returns true if the date is a working day
 */
export function isWorkingDay(
  date: Date,
  calendar: ScheduleCalendar = DEFAULT_SCHEDULE_CALENDAR
): boolean {
  // Check if day of week is a working day
  const dayOfWeek = date.getDay();
  if (!calendar.workingDays.includes(dayOfWeek)) {
    return false;
  }

  // Check if it's a holiday
  const dateStr = formatLocalDate(date);
  if (calendar.holidays.includes(dateStr)) {
    return false;
  }

  return true;
}

/**
 * Add a number of working days to a start date
 * 
 * Working days skip weekends and holidays according to the calendar.
 * If n is 0, returns the start date (even if it's not a working day).
 * If n is positive, finds the nth working day after start.
 * If n is negative, finds the nth working day before start.
 * 
 * @param startDate - The starting date (Date object or YYYY-MM-DD string)
 * @param n - Number of working days to add (can be negative)
 * @param calendar - Calendar configuration (defaults to Mon-Fri, no holidays)
 * @returns Date string in YYYY-MM-DD format (local)
 */
export function addWorkingDays(
  startDate: Date | string,
  n: number,
  calendar: ScheduleCalendar = DEFAULT_SCHEDULE_CALENDAR
): string {
  const date = typeof startDate === 'string' ? parseLocalDate(startDate) : new Date(startDate);
  
  if (n === 0) {
    return formatLocalDate(date);
  }

  const direction = n > 0 ? 1 : -1;
  let remaining = Math.abs(n);

  while (remaining > 0) {
    date.setDate(date.getDate() + direction);
    if (isWorkingDay(date, calendar)) {
      remaining--;
    }
  }

  return formatLocalDate(date);
}

/**
 * Add a number of calendar days (not working days) to a start date
 * 
 * This is an explicit function for when you want calendar days, not working days.
 * Use this for things like material lead times where weeks are calendar weeks.
 * 
 * @param startDate - The starting date (Date object or YYYY-MM-DD string)
 * @param n - Number of calendar days to add (can be negative)
 * @returns Date string in YYYY-MM-DD format (local)
 */
export function addCalendarDays(startDate: Date | string, n: number): string {
  const date = typeof startDate === 'string' ? parseLocalDate(startDate) : new Date(startDate);
  date.setDate(date.getDate() + n);
  return formatLocalDate(date);
}

/**
 * Count the number of working days between two dates (exclusive of end)
 * 
 * @param startDate - Start date (Date object or YYYY-MM-DD string)
 * @param endDate - End date (Date object or YYYY-MM-DD string)
 * @param calendar - Calendar configuration
 * @returns Number of working days between start and end (exclusive of end)
 */
export function countWorkingDays(
  startDate: Date | string,
  endDate: Date | string,
  calendar: ScheduleCalendar = DEFAULT_SCHEDULE_CALENDAR
): number {
  const start = typeof startDate === 'string' ? parseLocalDate(startDate) : new Date(startDate);
  const end = typeof endDate === 'string' ? parseLocalDate(endDate) : new Date(endDate);
  
  if (start >= end) {
    return 0;
  }

  let count = 0;
  const current = new Date(start);
  
  while (current < end) {
    if (isWorkingDay(current, calendar)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }

  return count;
}

/**
 * Get today's date as YYYY-MM-DD in local time
 * 
 * @returns Today's date string in YYYY-MM-DD format (local)
 */
export function getTodayLocalDate(): string {
  return formatLocalDate(new Date());
}

/**
 * Convenience wrapper for formatting a Date (or now) as local YYYY-MM-DD.
 * Ensures we never use UTC-based ISO truncation (toISOString().split('T')[0]).
 */
export function toLocalYYYYMMDD(date?: Date): string {
  return formatLocalDate(date ?? new Date());
}

/**
 * Calculate the end date for a schedule given start date, work days, and buffer days
 * 
 * This is the primary calculation function for ScheduleGenerator.
 * Uses INCLUSIVE semantics: a 1-day job starting Monday ends Monday (not Tuesday).
 * 
 * The calendar display logic uses inclusive range checking (start <= day <= end),
 * so this function computes end date accordingly.
 * 
 * @param startDate - The start date (Date object or YYYY-MM-DD string)
 * @param workDaysNeeded - Number of working days for the work itself
 * @param adminBufferDays - Number of working days for admin/mobilization buffer
 * @param calendar - Calendar configuration (defaults to Mon-Fri, no holidays)
 * @returns Date string in YYYY-MM-DD format (local) - inclusive end date
 */
export function calculateScheduleEndDate(
  startDate: Date | string,
  workDaysNeeded: number,
  adminBufferDays: number,
  calendar: ScheduleCalendar = DEFAULT_SCHEDULE_CALENDAR
): string {
  const totalWorkingDays = adminBufferDays + workDaysNeeded;
  
  // Inclusive semantics: if total is 0 or less, end = start
  // If total is N days, end = start + (N-1) working days
  // Example: 1-day job (total=1) → end = start + 0 = start
  // Example: 5-day job (total=5) → end = start + 4 working days
  if (totalWorkingDays <= 0) {
    return typeof startDate === 'string' ? startDate : formatLocalDate(startDate);
  }
  
  return addWorkingDays(startDate, totalWorkingDays - 1, calendar);
}

/**
 * Calculate the default start date based on material lead time
 * 
 * Material lead times are in weeks (calendar weeks), so we add calendar days.
 * If materials have no lead time, start date is today.
 * 
 * @param maxLeadTimeWeeks - Maximum material lead time in weeks
 * @returns Date string in YYYY-MM-DD format (local)
 */
export function calculateDefaultStartDate(maxLeadTimeWeeks: number): string {
  const today = new Date();
  return addCalendarDays(today, maxLeadTimeWeeks * 7);
}

/**
 * Compare two date strings (YYYY-MM-DD format)
 * 
 * @param dateA - First date string
 * @param dateB - Second date string
 * @returns -1 if dateA < dateB, 0 if equal, 1 if dateA > dateB
 */
export function compareDateStrings(dateA: string, dateB: string): number {
  if (dateA < dateB) return -1;
  if (dateA > dateB) return 1;
  return 0;
}

/**
 * Check if a date string is in the past (before today)
 * 
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns true if the date is before today (local time)
 */
export function isDateInPast(dateStr: string): boolean {
  const today = getTodayLocalDate();
  return dateStr < today;
}

/**
 * Calculate the working days duration between a start and end date (inclusive)
 * 
 * This is the inverse of calculateScheduleEndDate - it tells you how many
 * working days a schedule spans. Used for drag-and-drop to preserve duration.
 * 
 * @param startDate - Start date (Date object or YYYY-MM-DD string)
 * @param endDate - End date (Date object or YYYY-MM-DD string)
 * @param calendar - Calendar configuration (defaults to Mon-Fri, no holidays)
 * @returns Number of working days in the range (inclusive)
 */
export function calculateWorkingDaysDuration(
  startDate: Date | string,
  endDate: Date | string,
  calendar: ScheduleCalendar = DEFAULT_SCHEDULE_CALENDAR
): number {
  const start = typeof startDate === 'string' ? parseLocalDate(startDate) : new Date(startDate);
  const end = typeof endDate === 'string' ? parseLocalDate(endDate) : new Date(endDate);
  
  // Normalize to midnight
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  if (start > end) {
    return 0;
  }

  let count = 0;
  const current = new Date(start);
  
  // Inclusive counting: count from start through end
  while (current <= end) {
    if (isWorkingDay(current, calendar)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }

  return count;
}
